import { Button, Spinner } from '@heroui/react';
import { FileText, Mail, Sparkles, Trash2 } from 'lucide-react';
import React, { FC } from 'react';

import { InMailTemplateRecord, useDeleteInMailTemplate, useGetInMailTemplates } from '../../../../queries';
import { EMAIL_TEMPLATES } from '../templates';

interface TemplatePickerProps {
  onPick: (templateId: string) => void;
  onOpenSaved: (record: InMailTemplateRecord) => void;
  onGenerateWithAi: () => void;
}

const formatDate = (value: number | null): string => (value
  ? new Date(value).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })
  : 'przed chwilą');

/** Ekran startowy kreatora: własne szablony na górze, wzory startowe pod nimi. */
export const TemplatePicker: FC<TemplatePickerProps> = ({ onPick, onOpenSaved, onGenerateWithAi }) => {
  const { data: saved = [], isLoading } = useGetInMailTemplates();
  const { mutate: removeTemplate } = useDeleteInMailTemplate();

  return (
    <div className="flex flex-col gap-8">
      <section className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Mail className="h-5 w-5 text-gray-500" />
          <div>
            <h2 className="text-base font-semibold text-gray-900">Twoje szablony</h2>
            <p className="text-sm text-gray-500">Zapisane w panelu, gotowe do ponownego użycia.</p>
          </div>
          <Button
            size="sm"
            color="primary"
            className="ml-auto"
            startContent={<Sparkles className="h-4 w-4" />}
            onPress={onGenerateWithAi}
          >
            Napisz z AI
          </Button>
        </div>

        {isLoading && <Spinner size="sm" className="self-start" />}

        {!isLoading && saved.length === 0 && (
          <p className="rounded-xl border border-dashed border-gray-200 p-5 text-sm text-gray-500">
            Nie masz jeszcze zapisanych szablonów. Zacznij od wzoru poniżej i zapisz go w kreatorze.
          </p>
        )}

        {saved.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {saved.map((record) => (
              <div
                key={record.id}
                className="flex flex-col justify-between gap-4 rounded-xl border border-gray-200 bg-white p-5"
              >
                <div className="flex flex-col gap-1">
                  <h3 className="text-sm font-semibold text-gray-900">{record.name}</h3>
                  <p className="truncate text-xs text-gray-500">{record.subject}</p>
                  <p className="text-[11px] text-gray-400">{`Zapisano ${formatDate(record.updatedAt)}`}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" color="primary" className="flex-1" onPress={() => onOpenSaved(record)}>
                    Otwórz
                  </Button>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    color="danger"
                    aria-label="Usuń szablon"
                    onPress={() => removeTemplate({ id: record.id })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="flex flex-col gap-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Wzory startowe</h2>
          <p className="text-sm text-gray-500">
            Wzór wpada do kreatora jako zwykłe bloki, więc zmienisz w nim wszystko.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {EMAIL_TEMPLATES.map((template) => (
            <div
              key={template.id}
              className="flex flex-col justify-between gap-4 rounded-xl border border-gray-200 bg-white p-5"
            >
              <div className="flex flex-col gap-2">
                <FileText className="h-5 w-5 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900">{template.name}</h3>
                <p className="text-xs leading-relaxed text-gray-500">{template.description}</p>
              </div>
              <Button size="sm" variant="bordered" onPress={() => onPick(template.id)}>
                Zacznij od tego
              </Button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
